//
// # KeyboardHelpPanel
//
// IDE sidebar panel showing available keyboard shortcuts.
// Displays shortcuts grouped by category, filtered by the current editing
// context (schematic vs layout vs route mode, etc.).
//
// Positioned in the right sidebar as a tab alongside the AI Assistant.
//

import React from "react";
import { Keyboard } from "lucide-react";
import { useWorkspace, useWorkspaceSnapshot } from "../../context";
import { getEditorContext, scopeMatchesContext } from "../../editorContext";
import { shortcutCategories } from "./shortcut-data";
import "./keyboardHelpPanel.css";

export function KeyboardHelpPanel() {
  const workspace = useWorkspace();
  const snapshot = useWorkspaceSnapshot();
  const hasActiveEditor = snapshot.activeIdent !== null;

  const ctx = getEditorContext(workspace);
  const inRouteMode = ctx.isRouteMode;

  // Filter categories by the current editing context. In route mode the idle
  // selection/placement gestures are replaced by routing interactions, so show
  // only route-specific and universal (global) categories.
  const visibleCategories = shortcutCategories.filter((cat) => {
    if (inRouteMode) {
      return cat.scopes.includes("route") || cat.scopes.includes("global");
    }
    return scopeMatchesContext(cat.scopes, ctx);
  });

  return (
    <div className="flex flex-col h-full bg-[var(--color-ide-surface)] text-[var(--color-ide-text)]">
      {/* Panel header */}
      <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-[var(--color-ide-text-muted)] select-none border-b border-[var(--color-ide-border)]">
        <div className="flex items-center gap-1.5">
          <Keyboard size={11} />
          <span className="flex-1">
            {inRouteMode ? "Shortcuts (Route Mode)" : "Keyboard Shortcuts"}
          </span>
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        {!hasActiveEditor && (
          <div className="p-3 text-[11px] text-[var(--color-ide-text-dim)]">
            Open a design to view shortcuts.
          </div>
        )}

        {hasActiveEditor && (
          <div className="p-1">
            {visibleCategories.map((category) => (
              <div key={category.name}>
                <div className="kb-panel-category">{category.name}</div>
                <div className="kb-panel-grid">
                  {category.shortcuts.map((shortcut) => (
                    <div key={shortcut.key} className="kb-panel-row">
                      <span className="kb-panel-key">{shortcut.key}</span>
                      <span className="kb-panel-desc">
                        {shortcut.description}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
